// Minimal DOM HUD overlaid on the canvas. DOM text is cheaper than re-rasterizing
// Pixi Text every frame and easy to style; the stats line updates each frame, the
// centered overlay only when its state changes.

import type { World } from '../game/world';
import { ABILITIES } from '../game/activeAbilities';
import type { AbilityDef } from '../game/balance';
import { makeIconCanvas, ABILITY_ICONS, drawPixels } from './icons';
import { getInputMode, onInputMode, PAD_ABILITY_LABELS } from '../core/inputMode';
import { isCompactTouchLayout, isShortTouchLayout } from './platform';

// Pixel-art icons for the HP / XP bars.
// prettier-ignore
/** Stacked boss health bars shown at once; more simultaneous bosses is not a real state. */
const MAX_BOSS_BARS = 4;

const HEART = [
  '.oo...oo.',
  'owwo.oRRo',
  'owRRoRRRo',
  'oRRRRRRRo',
  '.oRRRRRo.',
  '..oRRRo..',
  '...oRo...',
  '....o....',
];
const HEART_PAL: Record<string, string> = { o: '#400a12', R: '#ff2f3e', w: '#ffd9dd' };
// prettier-ignore
const GEM = [
  '...o...',
  '..oBo..',
  '.owLBo.',
  'oBLLLBo',
  '.oBLBo.',
  '..oBo..',
  '...o...',
];
const GEM_PAL: Record<string, string> = { o: '#0a2438', B: '#3aa0e0', L: '#bdeaff', w: '#ffffff' };

export class Hud {
  private readonly stats: HTMLDivElement; // time line under the bars
  private readonly lvEl: HTMLDivElement;
  private readonly killsEl: HTMLDivElement;
  private readonly hpDraw: (ratio: number) => void;
  private readonly hpLabel: HTMLDivElement;
  private readonly xpDraw: (ratio: number) => void;
  private readonly xpLabel: HTMLDivElement;
  private readonly center: HTMLDivElement;
  private readonly bossWrap: HTMLDivElement;
  private readonly bossBars: { wrap: HTMLDivElement; fill: HTMLDivElement }[] = [];
  private readonly banner: HTMLDivElement;
  private readonly hurtFlash: HTMLDivElement;
  private readonly abilitySlots: {
    root: HTMLDivElement;
    icon: HTMLCanvasElement;
    key: HTMLDivElement;
    sub: HTMLDivElement;
    cooldownFill: HTMLDivElement;
    cooldownLabel: HTMLDivElement;
  }[] = [];
  private readonly tip: HTMLDivElement;
  private readonly compact: boolean;
  private playerLevel = 1;
  private allAbilities = false;
  private abilityDefs: ReadonlyArray<AbilityDef> = ABILITIES;
  private centerState = '';
  private onResume: (() => void) | null = null;

  constructor(mount: HTMLElement, onAbility?: (index: number) => void) {
    // Compact touch layouts narrow the bars + shrink labels so iPhone WebView corners stay clear.
    const compact = isCompactTouchLayout();
    const shortTouch = isShortTouchLayout();
    this.compact = compact;
    const barW = compact ? (shortTouch ? 52 : 58) : 150;

    // Full-screen red flash when the player is hit (opacity driven by world.hurtFlashSec).
    this.hurtFlash = document.createElement('div');
    Object.assign(this.hurtFlash.style, {
      position: 'absolute',
      inset: '0',
      background: '#ff2233',
      opacity: '0',
      pointerEvents: 'none',
    });
    mount.appendChild(this.hurtFlash);

    // Top HUD: LV · HP/XP bars · time · kills. Compact/iOS keeps everything in one
    // shallow row so the playfield has more vertical space.
    const top = document.createElement('div');
    top.className = 'hud-top'; // CSS pins it left under 500px (see play.html); centered otherwise
    Object.assign(top.style, {
      position: 'absolute',
      top: compact ? '8px' : '8px',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      alignItems: 'center',
      gap: compact ? '12px' : '16px',
      pointerEvents: 'none',
      userSelect: 'none',
    });

    this.lvEl = document.createElement('div');
    Object.assign(this.lvEl.style, {
      font: `${compact ? 12 : 20}px "Silkscreen", ui-monospace, monospace`,
      color: '#ffd9a0',
      textShadow: '0 2px 3px #000',
      minWidth: compact ? '18px' : '70px',
      textAlign: 'right',
    });

    // On desktop the bars are stacked. On compact/iOS they sit side-by-side to reduce HUD height.
    const barsCol = document.createElement('div');
    Object.assign(barsCol.style, {
      position: 'relative',
      display: 'flex',
      flexDirection: compact ? 'row' : 'column',
      alignItems: 'center',
      gap: compact ? '14px' : '4px',
    });
    const hp = makeBar(
      HEART,
      HEART_PAL,
      barW,
      11,
      2,
      { outline: '#140f1f', track: '#e6e0ee', fillTop: '#ff6a74', fillBody: '#e01e2e' },
      compact ? { iconMode: 'inline', iconScale: 3, iconGap: 5, labelFontPx: 8 } : undefined,
    );
    this.hpDraw = hp.draw;
    this.hpLabel = hp.label;
    const xp = makeBar(
      GEM,
      GEM_PAL,
      barW,
      11,
      2,
      { outline: '#140f1f', track: '#e6e0ee', fillTop: '#a8e4ff', fillBody: '#3aa8e0' },
      compact ? { iconMode: 'inline', iconScale: 3, iconGap: 5, labelFontPx: 8 } : undefined,
    );
    this.xpDraw = xp.draw;
    this.xpLabel = xp.label;
    if (compact) {
      Object.assign(this.hpLabel.style, { font: '8px "Silkscreen", ui-monospace, monospace' });
      Object.assign(this.xpLabel.style, { font: '8px "Silkscreen", ui-monospace, monospace' });
    }
    this.stats = document.createElement('div');
    Object.assign(this.stats.style, {
      position: compact ? 'static' : 'absolute',
      top: compact ? 'auto' : '100%',
      left: compact ? 'auto' : '0',
      right: compact ? 'auto' : '0',
      marginTop: compact ? '0' : '3px',
      textAlign: 'center',
      font: `${compact ? 8 : 12}px "Silkscreen", ui-monospace, monospace`,
      color: '#cfc8e0',
      textShadow: '0 1px 2px #000',
      minWidth: compact ? '36px' : 'auto',
    });
    barsCol.append(hp.holder, xp.holder, this.stats);

    this.killsEl = document.createElement('div');
    Object.assign(this.killsEl.style, {
      font: `${compact ? 10 : 20}px "Silkscreen", ui-monospace, monospace`,
      color: '#e8e4f0',
      textShadow: '0 2px 3px #000',
      minWidth: compact ? '22px' : '100px',
      textAlign: 'left',
    });

    top.append(this.lvEl, barsCol, this.killsEl);
    mount.appendChild(top);

    this.center = document.createElement('div');
    Object.assign(this.center.style, {
      position: 'absolute',
      inset: '0',
      display: 'none',
      placeItems: 'center',
      textAlign: 'center',
      font: `${compact ? 18 : 22}px "Silkscreen", ui-monospace, monospace`,
      color: '#fff',
      background: 'rgba(10,8,16,0.55)',
      pointerEvents: 'none',
      userSelect: 'none',
    });
    mount.appendChild(this.center);

    // Boss health bars (top-center, below the stats line) - one stacked row per living
    // boss, so an undefeated boss never loses its bar when the next one arrives.
    this.bossWrap = document.createElement('div');
    Object.assign(this.bossWrap.style, {
      position: 'absolute',
      top: compact ? '34px' : '62px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '46%',
      display: 'none',
      flexDirection: 'column',
      gap: '3px',
      pointerEvents: 'none',
    });
    for (let i = 0; i < MAX_BOSS_BARS; i++) {
      const wrap = document.createElement('div');
      Object.assign(wrap.style, {
        height: compact ? '9px' : '12px',
        display: 'none',
        border: '1px solid #5a2030',
        background: 'rgba(20,8,12,0.85)',
      });
      const fill = document.createElement('div');
      Object.assign(fill.style, { height: '100%', width: '100%', background: '#ff3b4e' });
      wrap.appendChild(fill);
      this.bossWrap.appendChild(wrap);
      this.bossBars.push({ wrap, fill });
    }
    mount.appendChild(this.bossWrap);

    // Transient event/boss banner.
    this.banner = document.createElement('div');
    Object.assign(this.banner.style, {
      position: 'absolute',
      top: '22%',
      left: '0',
      right: '0',
      textAlign: 'center',
      display: 'none',
      font: `700 ${compact ? 20 : 30}px "Silkscreen", ui-monospace, monospace`,
      letterSpacing: compact ? '2px' : '3px',
      color: '#ffd9a0',
      textShadow: '0 2px 8px #000',
      pointerEvents: 'none',
      userSelect: 'none',
    });
    mount.appendChild(this.banner);

    // Tooltip shown when hovering an ability slot (e.g. while paused).
    this.tip = document.createElement('div');
    Object.assign(this.tip.style, {
      position: 'absolute',
      display: 'none',
      maxWidth: '230px',
      padding: '8px 10px',
      border: '1px solid #4a4366',
      borderRadius: '6px',
      background: 'rgba(14,11,22,0.96)',
      font: '12px "Silkscreen", ui-monospace, monospace',
      color: '#e8e4f0',
      lineHeight: '1.4',
      pointerEvents: 'none',
      userSelect: 'none',
      zIndex: '10',
      textShadow: '0 1px 2px #000',
    });
    mount.appendChild(this.tip);

    // Ability bar - vertical on the left edge; CSS flips it to a centered bottom row in
    // landscape (short screens) so it's reachable + on-screen on phones (see play.html).
    const bar = document.createElement('div');
    bar.className = 'ability-bar';
    Object.assign(bar.style, {
      position: 'absolute',
      left: compact ? '8px' : '10px',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      flexDirection: 'column',
      gap: compact ? (shortTouch ? '12px' : '8px') : '6px',
      userSelect: 'none',
    });
    for (let i = 0; i < ABILITIES.length; i++) {
      const def = ABILITIES[i];
      const hex = `#${def.color.toString(16).padStart(6, '0')}`;
      const root = document.createElement('div');
      Object.assign(root.style, {
        width: compact ? (shortTouch ? '44px' : '40px') : '52px',
        height: compact ? (shortTouch ? '38px' : '34px') : 'auto',
        padding: compact ? '0' : '4px 0',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid #2a2438',
        borderRadius: '5px',
        background: 'rgba(16,12,24,0.82)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1px',
        font: `${compact ? 10 : 12}px "Silkscreen", ui-monospace, monospace`,
        color: '#cfc8e0',
        pointerEvents: 'auto',
        cursor: 'pointer',
        touchAction: 'none',
      });
      root.setAttribute('role', 'button');
      root.setAttribute('aria-label', `${def.name}. Locked until level ${def.unlockLevel}.`);
      root.setAttribute('aria-disabled', 'true');
      root.tabIndex = 0;
      const cooldownFill = document.createElement('div');
      Object.assign(cooldownFill.style, {
        position: 'absolute',
        left: '0',
        bottom: '0',
        width: '0%',
        height: compact ? '4px' : '3px',
        background: hex,
        opacity: compact ? '0.82' : '0.55',
        pointerEvents: 'none',
        zIndex: '1',
      });
      const icon = makeIconCanvas(ABILITY_ICONS[def.key], def.color, compact ? 3 : 2);
      Object.assign(icon.style, { position: 'relative', zIndex: '2' });
      root.append(cooldownFill, icon);
      const key = document.createElement('div');
      key.textContent = def.label;
      Object.assign(key.style, { display: compact ? 'none' : 'block', fontSize: '14px', fontWeight: '700', lineHeight: '1', position: 'relative', zIndex: '2' });
      const sub = document.createElement('div');
      Object.assign(sub.style, { display: compact ? 'none' : 'block', fontSize: '9px', opacity: '0.85', position: 'relative', zIndex: '2' });
      const cooldownLabel = document.createElement('div');
      Object.assign(cooldownLabel.style, {
        position: 'absolute',
        inset: '0',
        display: 'none',
        placeItems: 'center',
        color: '#fff',
        font: `${compact ? 10 : 9}px "Silkscreen", ui-monospace, monospace`,
        textShadow: '-1px -1px 0 #14101f, 1px -1px 0 #14101f, -1px 1px 0 #14101f, 1px 1px 0 #14101f',
        background: compact ? 'rgba(8,6,14,0.34)' : 'transparent',
        pointerEvents: 'none',
        zIndex: '3',
      });
      root.appendChild(key);
      root.appendChild(sub);
      root.appendChild(cooldownLabel);
      if (!compact) {
        root.addEventListener('mouseenter', () => {
          const activeDef = this.abilityDefs[i];
          const activeHex = `#${activeDef.color.toString(16).padStart(6, '0')}`;
          const r = root.getBoundingClientRect();
          const have = this.playerLevel >= activeDef.unlockLevel || this.allAbilities;
          const status = have
            ? `Ready · press ${activeDef.label} · ${activeDef.cooldownSec}s cooldown`
            : `Unlocks at level ${activeDef.unlockLevel} (key ${activeDef.label})`;
          this.tip.innerHTML = `<b style="color:${activeHex}">${activeDef.name}</b><br>${activeDef.desc}<br><span style="opacity:.7">${status}</span>`;
          this.tip.style.left = `${r.right + 8}px`;
          this.tip.style.top = `${r.top}px`;
          this.tip.style.display = 'block';
        });
        root.addEventListener('mouseleave', () => {
          this.tip.style.display = 'none';
        });
      }
      // Tap/click to cast (the only way to use abilities on touch devices).
      const castAbility = (): void => {
        this.tip.style.display = 'none';
        onAbility?.(i);
      };
      let lastTouchCast = -Infinity;
      root.addEventListener('touchstart', (event) => {
        event.preventDefault();
        event.stopPropagation();
        lastTouchCast = performance.now();
        castAbility();
      }, { passive: false });
      root.addEventListener('click', () => {
        // WebKit may synthesize a click after touchstart. The touch already cast the
        // ability, so ignore that follow-up while retaining ordinary mouse clicks.
        if (performance.now() - lastTouchCast < 700) return;
        castAbility();
      });
      root.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        castAbility();
      });
      bar.appendChild(root);
      this.abilitySlots.push({ root, icon, key, sub, cooldownFill, cooldownLabel });
    }
    mount.appendChild(bar);

    // Show controller button prompts (A/B/X/Y/LB/RB/LT/RT) when a gamepad is in use,
    // keyboard letters (A/S/D/F/Z/X/C/V) otherwise.
    this.syncAbilityKeys();
    onInputMode(() => this.syncAbilityKeys());

    // Movement legend - arrow keys laid out as a d-pad on the right edge.
    const moveWrap = document.createElement('div');
    Object.assign(moveWrap.style, {
      position: 'absolute',
      right: '14px',
      bottom: '14px',
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 28px)',
      gridAutoRows: '28px',
      gap: '4px',
      pointerEvents: 'none',
      userSelect: 'none',
      opacity: '0.9',
    });
    const moveLabel = document.createElement('div');
    moveLabel.textContent = 'MOVE';
    Object.assign(moveLabel.style, {
      gridColumn: '1 / 4',
      textAlign: 'center',
      font: '10px "Silkscreen", ui-monospace, monospace',
      color: '#9a93b0',
      letterSpacing: '2px',
    });
    moveWrap.appendChild(moveLabel);
    const up = keyCap('↑');
    up.style.gridColumn = '2';
    up.style.gridRow = '2';
    const left = keyCap('←');
    left.style.gridColumn = '1';
    left.style.gridRow = '3';
    const down = keyCap('↓');
    down.style.gridColumn = '2';
    down.style.gridRow = '3';
    const right = keyCap('→');
    right.style.gridColumn = '3';
    right.style.gridRow = '3';
    moveWrap.append(up, left, down, right);
    moveWrap.className = 'hud-move'; // hidden on touch (see play.html); the joystick replaces it
    mount.appendChild(moveWrap);

    // Pause hint - top-left corner.
    const pauseHint = document.createElement('div');
    Object.assign(pauseHint.style, {
      position: 'absolute',
      left: '10px',
      top: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      pointerEvents: 'none',
      userSelect: 'none',
    });
    const escCap = document.createElement('div');
    escCap.textContent = 'ESC';
    Object.assign(escCap.style, {
      padding: '2px 6px',
      border: '1px solid #3a3450',
      borderRadius: '4px',
      background: 'rgba(16,12,24,0.82)',
      font: '11px "Silkscreen", ui-monospace, monospace',
      color: '#cfc8e0',
    });
    const pauseLbl = document.createElement('div');
    pauseLbl.textContent = 'Pause';
    Object.assign(pauseLbl.style, {
      font: '11px "Silkscreen", ui-monospace, monospace',
      color: '#9a93b0',
      letterSpacing: '1px',
    });
    pauseHint.append(escCap, pauseLbl);
    pauseHint.className = 'hud-esc'; // hidden on touch (no ESC key; pause via MENU)
    mount.appendChild(pauseHint);
  }

  setResumeHandler(handler: () => void): void {
    this.onResume = handler;
  }

  // Swap each ability slot's key glyph between the keyboard letter and the controller button.
  private syncAbilityKeys(): void {
    const pad = getInputMode() === 'gamepad';
    for (let i = 0; i < this.abilitySlots.length; i++) {
      const key = this.abilitySlots[i].key;
      if (pad && i < PAD_ABILITY_LABELS.length) {
        const b = PAD_ABILITY_LABELS[i];
        key.textContent = b.label;
        Object.assign(key.style, { background: b.color, color: '#15131f', borderRadius: '3px', padding: '0 4px' });
      } else {
        key.textContent = this.abilityDefs[i].label;
        Object.assign(key.style, { background: 'transparent', color: '#cfc8e0', borderRadius: '0', padding: '0' });
      }
    }
  }

  private updateAbilityBar(world: World): void {
    this.setAbilityRoster(world.abilityDefs);
    const ab = world.abilities;
    for (let i = 0; i < this.abilityDefs.length; i++) {
      const def = this.abilityDefs[i];
      const s = this.abilitySlots[i];
      const hex = `#${def.color.toString(16).padStart(6, '0')}`;
      if (world.level < def.unlockLevel && !world.allAbilities) {
        s.root.style.opacity = '0.32';
        s.root.style.borderColor = '#2a2438';
        s.root.style.boxShadow = 'none';
        s.key.style.color = '#6a6480';
        s.sub.textContent = `L${def.unlockLevel}`;
        s.cooldownFill.style.width = '0%';
        s.cooldownLabel.style.display = 'none';
        s.cooldownLabel.textContent = '';
        s.root.setAttribute('aria-label', `${def.name}. Locked until level ${def.unlockLevel}.`);
        s.root.setAttribute('aria-disabled', 'true');
        continue;
      }
      const cd = Math.max(0, ab.cd[i]);
      const active = Math.max(0, ab.active[i]);
      const cdRatio = def.cooldownSec > 0 ? Math.min(cd / def.cooldownSec, 1) : 0;
      const cooldownText = String(Math.ceil(cd));

      s.root.style.opacity = '1';
      s.root.setAttribute('aria-disabled', 'false');
      s.cooldownFill.style.background = hex;
      s.cooldownFill.style.width = cd > 0 ? `${Math.round(cdRatio * 100)}%` : '0%';
      s.cooldownLabel.style.display = this.compact && cd > 0 ? 'grid' : 'none';
      s.cooldownLabel.textContent = this.compact && cd > 0 ? cooldownText : '';

      if (active > 0) {
        s.root.style.borderColor = hex;
        s.root.style.boxShadow = this.compact ? 'none' : `0 0 8px ${hex}`;
        s.key.style.color = hex;
        s.sub.textContent = cd > 0 ? `${Math.ceil(cd)}s` : `${active.toFixed(1)}s`;
        s.root.setAttribute('aria-label', `${def.name}. Active. ${Math.ceil(cd)} seconds until ready.`);
      } else if (cd > 0) {
        s.root.style.borderColor = hex; // unlocked → keep the colored border while cooling
        s.root.style.boxShadow = 'none';
        s.key.style.color = '#6a6480';
        s.sub.textContent = `${Math.ceil(cd)}s`;
        s.root.setAttribute('aria-label', `${def.name}. Cooling down. ${cooldownText} seconds remaining.`);
      } else {
        s.root.style.borderColor = hex;
        s.root.style.boxShadow = 'none';
        s.key.style.color = '#ffffff';
        s.sub.textContent = 'READY';
        s.root.setAttribute('aria-label', `${def.name}. Ready to cast.`);
      }
    }
  }

  private setAbilityRoster(defs: ReadonlyArray<AbilityDef>): void {
    if (this.abilityDefs === defs) return;
    this.abilityDefs = defs;
    for (let i = 0; i < this.abilitySlots.length; i++) {
      const def = defs[i];
      const slot = this.abilitySlots[i];
      if (def === undefined) {
        slot.root.style.display = 'none';
        continue;
      }
      slot.root.style.display = 'flex';
      const icon = makeIconCanvas(ABILITY_ICONS[def.key], def.color, this.compact ? 3 : 2);
      Object.assign(icon.style, { position: 'relative', zIndex: '2' });
      slot.root.replaceChild(icon, slot.icon);
      slot.icon = icon;
      slot.root.setAttribute('aria-label', `${def.name}. Locked until level ${def.unlockLevel}.`);
    }
    this.syncAbilityKeys();
  }

  update(world: World, paused: boolean): void {
    const t = world.elapsedSec;
    const mm = Math.floor(t / 60)
      .toString()
      .padStart(2, '0');
    const ss = Math.floor(t % 60)
      .toString()
      .padStart(2, '0');
    const hp = Math.max(0, Math.ceil(world.player.hp));
    this.hpDraw(hp / world.player.maxHp);
    this.hpLabel.textContent = `${hp}/${world.player.maxHp}`;
    this.xpDraw(world.xp / world.xpNeeded);
    this.xpLabel.textContent = `${world.xp}/${world.xpNeeded}`;
    this.lvEl.textContent = this.compact ? `L${world.level}` : `LV ${world.level}`;
    this.killsEl.textContent = this.compact ? `K${world.kills}` : `${world.kills} KILLS`;
    this.stats.textContent = `${mm}:${ss}`;

    // Red hit-flash (peaks on hit, fades with the hurt timer).
    this.hurtFlash.style.opacity = String(Math.min(0.42, world.hurtFlashSec * 1.4));

    // Boss health bars - one per living boss, tinted with that boss's color.
    const bosses = world.activeBosses;
    let bossBarsShown = 0;
    for (let i = 0; i < this.bossBars.length; i++) {
      const bar = this.bossBars[i];
      const boss = i < bosses.length ? bosses[i] : null;
      if (boss !== null && boss.hp > 0) {
        bar.wrap.style.display = 'block';
        bar.fill.style.width = `${Math.max(0, (boss.hp / boss.maxHp) * 100)}%`;
        bar.fill.style.background = `#${boss.color.toString(16).padStart(6, '0')}`;
        bossBarsShown++;
      } else {
        bar.wrap.style.display = 'none';
      }
    }
    this.bossWrap.style.display = bossBarsShown > 0 ? 'flex' : 'none';

    // Transient event/boss banner (fades out with bannerSec).
    if (world.bannerSec > 0) {
      this.banner.textContent = world.bannerText;
      this.banner.style.display = 'block';
      this.banner.style.opacity = String(Math.min(1, world.bannerSec));
    } else {
      this.banner.style.display = 'none';
    }

    this.playerLevel = world.level;
    this.allAbilities = world.allAbilities;
    this.updateAbilityBar(world);

    // The dedicated GameOverUI handles the death/victory screen; the HUD center is only pause.
    const state = world.gameOver ? 'over' : paused ? 'paused' : 'play';
    if (state === this.centerState) return;
    this.centerState = state;

    if (paused && !world.gameOver) {
      this.center.style.display = 'grid';
      this.center.replaceChildren();
      this.center.style.pointerEvents = 'none';

      const content = document.createElement('div');
      const title = document.createElement('div');
      title.textContent = 'PAUSED';
      content.appendChild(title);

      if (this.compact && this.onResume !== null) {
        this.center.style.pointerEvents = 'auto';
        const resume = document.createElement('button');
        resume.textContent = 'RESUME';
        resume.setAttribute('aria-label', 'Resume Brewmancer run');
        Object.assign(resume.style, {
          marginTop: '14px',
          minWidth: '150px',
          minHeight: '48px',
          padding: '10px 20px',
          border: '3px solid #6b4a08',
          borderRadius: '7px',
          background: '#f0a81e',
          color: '#2a1a00',
          font: '14px "Silkscreen", ui-monospace, monospace',
          cursor: 'pointer',
        });
        resume.addEventListener('click', () => this.onResume?.());
        content.appendChild(resume);
      } else {
        const hint = document.createElement('div');
        hint.textContent = 'ESC / P TO RESUME';
        Object.assign(hint.style, { marginTop: '7px', fontSize: '15px' });
        content.appendChild(hint);
      }

      this.center.appendChild(content);
    } else {
      this.center.style.display = 'none';
      this.center.style.pointerEvents = 'none';
    }
  }
}

// A small key-cap square showing a glyph (used for the movement legend).
function keyCap(glyph: string): HTMLDivElement {
  const d = document.createElement('div');
  d.textContent = glyph;
  Object.assign(d.style, {
    width: '28px',
    height: '28px',
    border: '1px solid #3a3450',
    borderRadius: '4px',
    background: 'rgba(16,12,24,0.82)',
    display: 'grid',
    placeItems: 'center',
    font: '15px "Silkscreen", ui-monospace, monospace',
    color: '#cfc8e0',
  });
  return d;
}

interface BarColors {
  outline: string;
  track: string;
  fillTop: string;
  fillBody: string;
}

interface BarOptions {
  iconMode?: 'overlap' | 'inline';
  iconScale?: number;
  iconGap?: number;
  labelFontPx?: number;
}

// Draw a pixel bar (chunky outline, pixel-stepped rounded corners, light track, colored
// fill with a lighter top row) at `ratio` fill. Logical grid is W×H, drawn at `scale`.
function drawBar(ctx: CanvasRenderingContext2D, w: number, h: number, scale: number, ratio: number, c: BarColors): void {
  ctx.clearRect(0, 0, w * scale, h * scale);
  const inset = (y: number): number => (y === 0 || y === h - 1 ? 2 : y === 1 || y === h - 2 ? 1 : 0);
  const inShape = (x: number, y: number): boolean => y >= 0 && y < h && x >= inset(y) && x <= w - 1 - inset(y);
  const fillPx = Math.round(ratio * w);
  for (let y = 0; y < h; y++) {
    for (let x = inset(y); x <= w - 1 - inset(y); x++) {
      const onEdge = !inShape(x - 1, y) || !inShape(x + 1, y) || !inShape(x, y - 1) || !inShape(x, y + 1);
      let col: string;
      if (onEdge) col = c.outline;
      else if (x < fillPx) col = y <= 2 ? c.fillTop : c.fillBody;
      else col = c.track;
      ctx.fillStyle = col;
      ctx.fillRect(x * scale, y * scale, scale, scale);
    }
  }
}

// A pixel-styled progress bar: a pixel-art icon overlapping the left end, a canvas-drawn
// pixel bar (so the rounding is stepped, not smooth), and an outlined centered label.
function makeBar(
  iconGrid: string[],
  iconPal: Record<string, string>,
  w: number,
  h: number,
  scale: number,
  colors: BarColors,
  options: BarOptions = {},
): { holder: HTMLDivElement; draw: (ratio: number) => void; label: HTMLDivElement } {
  const pxW = w * scale;
  const pxH = h * scale;
  const inlineIcon = options.iconMode === 'inline';
  const iconScale = options.iconScale ?? Math.max(2, Math.round((pxH + 10) / iconGrid.length));
  const icon = drawPixels(iconGrid, iconPal, iconScale);
  const iconGap = options.iconGap ?? 4;
  const holderW = inlineIcon ? icon.width + iconGap + pxW : pxW;
  const holderH = inlineIcon ? Math.max(pxH, icon.height) : pxH;
  const barLeft = inlineIcon ? icon.width + iconGap : 0;
  const barTop = Math.round((holderH - pxH) / 2);
  const holder = document.createElement('div');
  Object.assign(holder.style, { position: 'relative', width: `${holderW}px`, height: `${holderH}px`, flex: '0 0 auto' });

  const canvas = document.createElement('canvas');
  canvas.width = pxW;
  canvas.height = pxH;
  Object.assign(canvas.style, {
    position: 'absolute',
    left: `${barLeft}px`,
    top: `${barTop}px`,
    width: `${pxW}px`,
    height: `${pxH}px`,
    display: 'block',
    imageRendering: 'pixelated',
  });
  const ctx = canvas.getContext('2d');

  Object.assign(icon.style, {
    position: 'absolute',
    left: inlineIcon ? '0' : `${-Math.round(icon.width * 0.62)}px`,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: '2',
    filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.55))',
  });

  const label = document.createElement('div');
  Object.assign(label.style, {
    position: 'absolute',
    left: `${barLeft}px`,
    top: `${barTop}px`,
    width: `${pxW}px`,
    height: `${pxH}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: '1',
    paddingBottom: '1px', // optical centering for the pixel font
    font: `${options.labelFontPx ?? Math.max(9, pxH - 9)}px "Silkscreen", ui-monospace, monospace`,
    color: '#fff',
    textShadow: '-1px -1px 0 #14101f, 1px -1px 0 #14101f, -1px 1px 0 #14101f, 1px 1px 0 #14101f',
  });

  holder.append(canvas, icon, label);

  let lastFill = -1;
  const draw = (ratio: number): void => {
    const r = Math.max(0, Math.min(1, ratio));
    const fp = Math.round(r * w);
    if (fp === lastFill || ctx === null) return;
    lastFill = fp;
    drawBar(ctx, w, h, scale, r, colors);
  };
  draw(1);
  return { holder, draw, label };
}
